import { post_SubmitMLTAApplication } from "services/online-application-service.openapi.zod";
import { z } from "zod";
import { toSingleLetterFromLowerCaseGender } from "utils/conversions";
import type { ApplicationState } from "store/application/applicationSlice";
import {
    validateSubmissionState,
    buildSecondaryAddressee,
    buildApplicantIdentity,
    buildPrimaryBeneficiary,
} from "mappers/shared/submissionMapper.base";

const BodySchema = post_SubmitMLTAApplication.parameters.shape.body;
type LoyaltySubmissionRequestBody = z.infer<typeof BodySchema>;
type BackendTiers = z.infer<typeof BodySchema.shape.tierSelection>;

/**
 * Transforms MLTA Redux state into the format required for submission API
 * @param mltaState - The application state from Redux store
 * @returns Formatted submission request body
 */
export function transformMLTASubmissionData(mltaState: ApplicationState): LoyaltySubmissionRequestBody {
    const {
        quoteData,
        contactData,
        paymentInformationData,
        offerData,
        contactInformation,
        policyBeneficiary,
        secondaryAddressee,
        landingPageData,
    } = validateSubmissionState(mltaState, { requireOffer: true });

    // Additional data for tracking
    const additionalData = [
        {
            key: "applicantType",
            value: quoteData.applicantType,
        },
    ];

    return {
        applicationID: offerData.invitationCode,
        policyNumber: contactData.policyNumber!,

        faceAmount: quoteData.selectedCoverageAmount!,
        modalPremiumAmount: quoteData.selectedCoveragePremium!,

        applicant: {
            ...buildApplicantIdentity(contactInformation, quoteData.zipCode!), // MLTA: zipCode comes from quoteData (collected on quote page)
            // MLTA: gender comes from quoteData (collected on quote page), default to "M" if undefined
            gender: quoteData.gender ? toSingleLetterFromLowerCaseGender(quoteData.gender)! : "M",
            keyCode: quoteData.keyCode ?? offerData.keyCode,
            aaaMemberNumber: (landingPageData?.loyaltyParams?.aaaMemberNumber ?? offerData.aaaMemberNumber)!,

            // MLTA-specific fields (URL params take precedence, API response as fallback)
            memberJoinDate: landingPageData?.loyaltyParams?.memberSince ?? offerData.memberSince,
            membershipLevel: (landingPageData?.loyaltyParams?.membershipLength ?? quoteData.membershipLevel)!,
        },

        beneficiaries: [buildPrimaryBeneficiary(policyBeneficiary)],

        ...buildSecondaryAddressee(secondaryAddressee),

        paymentToken: paymentInformationData.paymentToken,
        paymentType: paymentInformationData.paymentType,
        lastFourDigits: paymentInformationData.lastFourDigits,
        acceptanceConfirmed: true, // User must confirm acceptance to reach submission

        campaign: {
            campaignType: "CAMPAIGNTYPE_DIRECTMAIL",
            productCode: (quoteData.planCode ?? offerData.planCode) as "MLTA" | "ACLTA",
            clubCode: landingPageData?.campaign.clubCode ?? quoteData.clubCode ?? contactData.clubCode!,
            leadSource: landingPageData?.campaign.leadSource,
            campaignName: "DIR",
            campaignCode: landingPageData?.campaign.campaignCode ?? offerData.campaignCode,
        },

        offerPurl: landingPageData?.campaign.offerPurl,

        signedTimestamp: new Date().toISOString(),
        applicationSubmissionTimestamp: new Date().toISOString(),

        additionalData,

        // MLTA-specific fields for tier selection
        tiers: quoteData.mltaBenefits?.map((benefit) => benefit.tier) as BackendTiers[],
        tierSelection: quoteData.selectedTier as BackendTiers,
        coverageTypeSelection: quoteData.selectedCoverageType!,
    };
}
